import { Moment, MomentInput } from 'moment';
import React, {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
  FC,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { Checkbox } from 'components/atoms/checkbox';
import { formatDate } from 'components/organisms/projects/project-plan/timeline/helpers';
import type { DateField } from 'components/organisms/tasks/fields';
import { updateTaskSagaAction } from 'data/tasks/actions';
import { useLocalStorage } from 'hooks/use-local-storage';
import type { Task } from 'stores/tasks';
import { DateType, getDuration, getNewDates } from 'utils/date-shift';
import { DateFieldEditorPopover } from './date-field-editor-popover';

const DateEditor = styled(DateFieldEditorPopover)`
  padding: 0 !important;
`;

interface Props {
  data: Task;
  title: ReactNode;
  onChange: any;
  ref: RefObject<any>;
  field: DateField;
  disabledDate: (current: Moment) => boolean;
  allowClear?: boolean;
}

const StyledDateEditor = styled(DateEditor)<{
  maintainDuration: boolean;
}>`
  height: ${(props) => props.maintainDuration && '440px'};
`;

export const AGGridDateEditor: FC<Props> = (props) => {
  const {
    title,
    field,
    onChange,
    disabledDate,
    data,
    allowClear = true,
  } = props;
  const dispatch = useDispatch();
  const [haveMaintainDurationCheckbox, setHaveMaintainDurationCheckbox] =
    useState(false);

  const location = useLocation();

  const path = useCallback(() => {
    if (
      location.pathname === `/projects/${data.project}/tasks/list` ||
      location.pathname === '/tasks'
    ) {
      setHaveMaintainDurationCheckbox(true);
    } else {
      setHaveMaintainDurationCheckbox(false);
    }
  }, [data.project, location.pathname]);

  useEffect(() => {
    path();
  }, [data.project, path]);

  const [checked, setChecked] = useLocalStorage(
    'use.maintain.duration.local.storage',
    true,
  );

  const handleCheckBoxChange = () => {
    setChecked(!checked);
  };

  const _duration = useMemo(
    () => getDuration(data.startDate, data.dueDate, false),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.startDate, data.dueDate],
  );

  const handleOnChange = useCallback(
    (latest: string | null) => {
      if (haveMaintainDurationCheckbox && checked) {
        const payload: { [key: string]: MomentInput | null } = {};

        if (latest !== null) {
          const [newStartDate, newDueDate] = getNewDates(
            latest,
            _duration,
            field.fieldName as unknown as DateType,
          );

          payload.startDate = newStartDate;
          payload.dueDate = newDueDate;
        } else if (field.fieldName === DateType.StartDate) {
          payload.startDate = null;
          payload.dueDate = formatDate(data.dueDate);
        } else {
          payload.startDate = formatDate(data.startDate);
          payload.dueDate = null;
        }

        dispatch(
          updateTaskSagaAction({
            projectId: data.project,
            taskId: data.taskId,
            data: payload,
          }),
        );
      }
      onChange(latest);
    },
    [
      haveMaintainDurationCheckbox,
      checked,
      onChange,
      field.fieldName,
      dispatch,
      data.project,
      data.taskId,
      data.dueDate,
      data.startDate,
      _duration,
    ],
  );

  return (
    <StyledDateEditor
      title={title}
      field={field}
      onChange={handleOnChange}
      disabledDate={
        !haveMaintainDurationCheckbox
          ? disabledDate
          : !checked
          ? disabledDate
          : undefined
      }
      allowClear={allowClear}
      maintainDuration={haveMaintainDurationCheckbox}
      extraFooter={() => {
        return (
          haveMaintainDurationCheckbox && (
            <Checkbox checked={checked} onChange={handleCheckBoxChange}>
              <FormattedMessage id="date.picker.maintain.duration" />
            </Checkbox>
          )
        );
      }}
    />
  );
};
